import React, { ReactNode, useState } from "react";
import { Input } from "antd";

import type {
  PasswordProps,
  TextAreaProps,
  SearchProps,
  InputProps,
} from "antd/es/input";
import styles from "./styles.module.scss";
import classNames from "classnames";

export interface TextFieldProps extends InputProps {
  label?: string; // Floating label text
  classNamesProps?: string[];
  isOutsideLabelShown?: boolean;
}

export interface TextFieldPasswordProps extends PasswordProps {
  label?: string; // Floating label text
}

export interface TextFieldTextAreaProps extends TextAreaProps {
  label?: string; // Floating label text
}

export interface TextFieldSearchProps extends SearchProps {
  label?: string; // Floating label text
}

const FloatingLabelWrapper: React.FC<{
  label?: string;
  isFocused: boolean;
  hasValue: boolean;
  children: ReactNode;
}> = ({ label, isFocused, hasValue, children }) => {
  return (
    <div className={styles.textfieldContainer}>
      {label && (
        <label
          className={`${styles.textfieldLabel} ${
            isFocused || hasValue ? styles.textfieldLabelActive : ""
          }`}
        >
          {label}
        </label>
      )}
      {children}
    </div>
  );
};

const TextFieldComponent: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  classNamesProps,
  isOutsideLabelShown = true,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
    onBlur?.(e);
  };

  // Here, ensure the label is always visible, even if the value is an empty string
  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <FloatingLabelWrapper
      label={isOutsideLabelShown ? label || placeholder : ""}
      isFocused={isFocused}
      hasValue={hasValue}
    >
      <Input
        {...rest}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={classNames(styles.textfieldInput, classNamesProps)}
      />
    </FloatingLabelWrapper>
  );
};

const PasswordField: React.FC<TextFieldPasswordProps> = ({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
    onBlur?.(e);
  };

  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <FloatingLabelWrapper
      label={label || placeholder}
      isFocused={isFocused}
      hasValue={hasValue}
    >
      <Input.Password
        {...rest}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={styles.textfieldInput}
      />
    </FloatingLabelWrapper>
  );
};

const TextAreaField: React.FC<TextFieldTextAreaProps> = ({
  label,
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
    onBlur?.(e);
  };

  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <FloatingLabelWrapper
      label={label || placeholder}
      isFocused={isFocused}
      hasValue={hasValue}
    >
      <Input.TextArea
        {...rest}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={styles.textfieldInput}
      />
    </FloatingLabelWrapper>
  );
};

const SearchField: React.FC<TextFieldSearchProps> = ({
  placeholder,
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
    onBlur?.(e);
  };

  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <FloatingLabelWrapper
      label={label || placeholder}
      isFocused={isFocused}
      hasValue={hasValue}
    >
      <Input.Search
        {...rest}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={styles.textfieldInput}
      />
    </FloatingLabelWrapper>
  );
};

export const TextField = Object.assign(TextFieldComponent, {
  Password: PasswordField,
  TextArea: TextAreaField,
  Search: SearchField,
});
